import { SessionService } from 'app/Services/session.service';
import {
  Component,
  AfterViewInit,
  Output,
  EventEmitter,
  OnDestroy,
} from "@angular/core";
import { Router } from "@angular/router";
import { AppSettings } from "../../app-settings";
import { timer, Subscription } from 'rxjs';
import { EventService } from "../../Services/event.service";


// Sticky analytics join key — do not change without telling marketing (see EVENTS.md).
const FLOW_NAME = 'Free Trial Onboarding';
// Canonical CTA prefix marketing expects on In-App Flow Completed / Clicked events.
// Navigation itself uses the Angular router (in-app, no reload); this string is only
// the analytics label so Mixpanel/Customer.io see the agreed destination URL.
const CTA_PREFIX = 'https://blitzkrieg.e-gmat.com/secure-lms/page/free_trial/activity/';

@Component({
  selector: "app-post-registration-splash-popup",
  templateUrl: "./post-registration-splash-popup.component.html",
  styleUrls: ["./post-registration-splash-popup.component.css"],
})
export class PostRegistrationSplashPopupComponent implements AfterViewInit, OnDestroy {
  cdnUrl: string = AppSettings.cdnUrl;
  @Output() hidePopupSplash = new EventEmitter();
  timer$ = timer(800);
  subscriptions: Subscription;

  /** Which screen is visible: main | verbal | quant | di | <terminals> */
  currentStep = 'main';
  /** Guard so we never fire two "leaving the flow" paths for one interaction. */
  private closed = false;

  constructor(private sessionService: SessionService, private router: Router, private eventService: EventService) {}

  ngAfterViewInit(): void {
    // Small delay so the externally-loaded analytics wrapper is present.
    this.subscriptions = this.timer$.subscribe(() => {
      this.track("In-App Flow Displayed", {});
      // Drives the app-level scroll lock (appcues-noscroll) while the flow is open.
      this.sessionService.updateUserAppcuesSplashScreenFlagCustom(true);
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
   * Fire an event through the app's standard wrapper. Never breaks the flow.
   * EventService dual-writes to Segment and RudderStack and swallows its own
   * errors, so the try/catch and the typeof guard that used to live here are no
   * longer needed.
   */
  private track(event: string, props: any): void {
    const payload = Object.assign({ flow_name: FLOW_NAME }, props || {});
    this.eventService.track(event, payload);
  }

  /** Advance to a chooser/terminal screen (fires Clicked with the current step). */
  goto(step: string, buttonText: string): void {
    this.track("In-App Flow Clicked", { button_text: buttonText, step_name: this.currentStep });
    this.currentStep = step;
  }

  /** Back link on sub-screens (fires Clicked: "Back" with the current step). */
  back(step: string, buttonText: string): void {
    this.track("In-App Flow Clicked", { button_text: buttonText, step_name: this.currentStep });
    this.currentStep = step;
  }

  /** Terminal "Start Learning" CTA: fire Clicked + Completed, then navigate in-app. */
  complete(activityId: string, buttonText: string): void {
    if (this.closed) { return; }
    const destinationUrl = CTA_PREFIX + activityId;
    this.track("In-App Flow Clicked", {
      button_text: buttonText,
      step_name: this.currentStep,
      destination_url: destinationUrl,
    });
    this.track("In-App Flow Completed", {
      step_name: this.currentStep,
      destination_url: destinationUrl,
    });
    this.leaveFlow();
    this.router.navigate(['/page/free_trial/activity', activityId]);
  }

  /** Marks the flow seen server-side (via parent) and releases the scroll lock. */
  private leaveFlow(): void {
    this.closed = true;
    this.sessionService.updateUserAppcuesSplashScreenFlagCustom(false);
    // Parent POSTs userAppcuesSplashScreenFlag:false so the flow never shows again.
    this.hidePopupSplash.emit();
  }
}
